import { useMemo } from "react";

import TierInfo from "components/TierInfo";
import Box from "ds/components/Box";
import { UserGuideGroupEnriched } from "shared/UserGuides/types";
import { UserGuideStartLocation } from "shared/UserGuides/analytics";

import UserGuideGroupChapterSection from "./ChapterSection";
import UserGuideGroupEmpty from "./Empty";

type UserGuideGroupProps = {
  userGuideGroup: UserGuideGroupEnriched;
  groupId: string;
  location: UserGuideStartLocation;
  variant?: "default" | "assistantPanel";
};

const UserGuideGroup = ({
  userGuideGroup,
  groupId,
  location,
  variant = "default",
}: UserGuideGroupProps) => {
  const { guideId: initiallyExpandedGuideId, chapterId: initiallyExpandedChapterId } = useMemo(
    () =>
      userGuideGroup.chapters
        ?.flatMap((chapter) =>
          chapter.guides.map((guide) => ({
            guideId: guide.id,
            chapterId: chapter.id,
            isCompleted: guide.isCompleted,
          }))
        )
        .find((guide) => !guide.isCompleted) ?? { guideId: "", chapterId: "" },

    [userGuideGroup.chapters]
  );

  const isEmpty = !userGuideGroup.chapters || userGuideGroup.chapters.length === 0;

  if (isEmpty) {
    return <UserGuideGroupEmpty variant={variant} />;
  }

  return (
    <>
      <TierInfo
        feature={userGuideGroup.requiredEntitlements}
        variant="promo"
        type={variant === "assistantPanel" ? "banner" : "callout"}
        notAdminTextPlaceholder="Ask your admin to upgrade your plan."
        margin={variant === "assistantPanel" ? "0 x-large" : "0"}
      >
        Some hands-on steps in this objectives need features your current plan doesn't include. Upgrade to run them end-to-end.
      </TierInfo>
      <Box direction="column" gap="xx-large" grow="1" scrollable>
        {userGuideGroup.chapters.map((chapter) => (
          <UserGuideGroupChapterSection
            key={chapter.id}
            chapter={chapter}
            groupId={groupId}
            location={location}
            initiallyCollapsed={initiallyExpandedChapterId !== chapter.id}
            initiallyExpandedGuideId={initiallyExpandedChapterId && initiallyExpandedGuideId}
            variant={variant}
          />
        ))}
      </Box>
    </>
  );
};

export default UserGuideGroup;
